import { proxy, snapshot, useSnapshot } from 'valtio';

import { devtoolsApi } from '#/dev/devtools';
import { mergeAnalyticsFeatureWithIntegrations } from '#/plugins/analytics/analyticsUtils';
import { pluginManager } from '#/plugins/pluginManager';
import { MainConfig } from '#/service/config/config.types';
import { initialiseDefaultSettings } from '#/service/user/settings';
import { ignoreError } from '#/service/utils';

import { initAuth } from '../auth/auth.store';
import { resetStore } from '../common';

interface BootStore {
  isBooted: boolean;
  isDictionaryReady: boolean;
  isPluginManagerReady: boolean;
}

const bootStore = proxy<BootStore>({
  isBooted: false,
  isDictionaryReady: false,
  isPluginManagerReady: false,
});

export const initialBootStore = snapshot(bootStore) as BootStore;

export const bootActions = {
  startBootstrap(config: MainConfig) {
    const {
      restrictions,
      more,
      integrations,
      features,
      integrationsVersionOverrides,
    } = config;
    const isGeoBlocked = restrictions.geoblocked;

    // we only need these steps if we're not geoblocked
    if (!isGeoBlocked) {
      // initialise default settings
      initialiseDefaultSettings(more.settings);

      let allLoadedPromise: Promise<
        { status: string; value?: any; reason?: any }[]
      >;

      if (integrationsVersionOverrides?.properties?.disableIntegrations) {
        pluginManager.loadFeaturePlugins(
          {
            ...features,
            // merge S3 feature configs and Core integration configs for analytics
            analytics: mergeAnalyticsFeatureWithIntegrations(config),
          },
          []
        );
        pluginManager.loadFeaturePlugins(features, []);
        allLoadedPromise = pluginManager.allPluginsLoadedPromise();
      } else {
        const result = mergeAnalyticsFeatureWithIntegrations(config);
        // start loading plugins
        allLoadedPromise = pluginManager.loadIntegrationPlugins(
          integrations,
          {
            ...features,
            // merge S3 feature configs and Core integration configs for analytics
            analytics: result,
          },
          config.legacyAnalyticsEvents,
          integrationsVersionOverrides?.integrations
        );
      }

      allLoadedPromise
        .then(() => {
          bootActions.setPluginManagerReady(true);
        })
        .catch(ignoreError);
    }

    !isGeoBlocked && initAuth();

    bootActions.setBooted(true);
    bootActions.setDictionaryReady(true);
  },
  setBooted(isBooted: boolean) {
    bootStore.isBooted = isBooted;
  },
  setDictionaryReady(isReady: boolean) {
    bootStore.isDictionaryReady = isReady;
  },
  setPluginManagerReady(isReady: boolean) {
    bootStore.isPluginManagerReady = isReady;
  },
  reset() {
    resetStore(bootStore, initialBootStore);
  },
};

export const useBootStore = () => useSnapshot(bootStore) as BootStore;

devtoolsApi.setStore('boot', bootStore, bootActions);
